var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useMemo, useEffect } from 'react';
import { useTransition } from '@react-spring/web';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import cx from 'classnames';
import { CloseIcon } from '../close-icon/close-icon';
import { borderRadius } from '../theme/border-radius';
import { shadow } from '../theme/shadow';
import { blur } from '../theme/css';
import { Box } from '../box/box';
import { Dot } from '../dot/dot';
import { Typography } from '..';
let id = 0;
export const Main = styled.div `
  cursor: pointer;
  -webkit-user-select: none;
  user-select: none;
  display: flex;
  align-items: center;
  height: 100%;
  justify-content: center;
`;
export const Container = styled.div `
  position: fixed;
  z-index: 1001;
  width: 0 auto;
  top: 30px;
  bottom: 0px;
  margin: 0 auto;
  left: 0px;
  right: 0px;
  display: flex;
  flex-direction: column;
  pointer-events: none;
  align-items: center;
`;
export const Message = styled(Box) `
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  border-radius: ${borderRadius.md};
  margin-top: 10px;
  padding-right: 32px;
  ${blur};
  box-shadow: ${shadow.lg};
`;
export const Content = styled.div `
  color: ${() => 'var(--text-color-0)'};
  opacity: 0.9;
  padding: 4px 16px;
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
  height: auto;
`;
export const Title = styled(Typography.Title) `
  margin: 16px 0px;
`;
let add;
const cancelMap = new Map();
export const info = (msg) => {
    add(Object.assign(Object.assign({}, msg), { type: 'info' }));
    return () => {
        var _a;
        (_a = cancelMap.get(msg.key)) === null || _a === void 0 ? void 0 : _a();
    };
};
export const success = (msg) => {
    add(Object.assign(Object.assign({}, msg), { type: 'success' }));
    return () => {
        var _a;
        (_a = cancelMap.get(msg.key)) === null || _a === void 0 ? void 0 : _a();
    };
};
export const cancel = (key) => {
    var _a;
    (_a = cancelMap.get(key)) === null || _a === void 0 ? void 0 : _a();
};
export function Toast(_a) {
    var { config = { tension: 125, friction: 20, precision: 0.1 }, timeout = 3000, children } = _a, props = __rest(_a, ["config", "timeout", "children"]);
    const refMap = useMemo(() => new WeakMap(), []);
    const [items, setItems] = useState([]);
    add = (msg) => {
        id += 1;
        setItems((state) => [...state, Object.assign(Object.assign({}, msg), { key: msg.key || id })]);
    };
    const transitions = useTransition(items, {
        from: { opacity: 0, height: 0, life: '100%' },
        keys: (item) => item.key,
        enter: (item) => (next, cancel) => __awaiter(this, void 0, void 0, function* () {
            cancelMap.set(item.key, cancel);
            yield next({ opacity: 1, height: refMap.get(item).offsetHeight });
            yield next({ life: '00%' });
        }),
        leave: [{ opacity: 0 }, { height: 0 }],
        onRest: (_result, _ctrl, item) => {
            setItems((state) => state.filter((i) => {
                return i.key !== item.key;
            }));
        },
        config: (_item, _index, phase) => (key) => phase === 'enter' && key === 'life' ? { duration: timeout } : config,
    });
    // FIXME: forward api in another way
    useEffect(() => {
        children(add);
    }, [children]);
    return createPortal(React.createElement(Container, { "data-role": "toast", className: cx('toast', props.className) }, transitions((_a, item) => {
        var { life: _life } = _a, style = __rest(_a, ["life"]);
        return (React.createElement(Message, { "data-role": "toast-message", style: style, transparent: true },
            React.createElement(Content, { "data-role": "toast-message-content", ref: (ref) => ref && refMap.set(item, ref) },
                React.createElement(Dot, { type: item.type }),
                React.createElement(Title, { h6: true }, item.title)),
            React.createElement(CloseIcon, { position: "absolute", top: 16, right: 8, onClick: (e) => {
                    e.stopPropagation();
                    if (cancelMap.has(item.key)) {
                        cancelMap.get(item.key)();
                    }
                } })));
    })), document.body);
}
